<template>
  <div class="gallery-images-section">
    <div id="gallery">
      <div class="container">
        <div
          v-if="section.header"
          class="row"
        >
          <div class="col-12">
            <h2 v-html="section.header" />
            <div
              class="sub-text"
              v-html="section.content"
            />
          </div>
        </div>
        <div class="gallery-holder">
          <div
            v-for="gallery in section.gallery"
            :key="gallery.ID"
            class="grid-item"
          >
            <img
              :src="gallery.gallery_image.url"
              :alt="gallery.gallery_image.alt"
              loading="lazy"
              @click="showSlide(gallery.index_number)"
              @keypress="showSlide(gallery.index_number)"
            >
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showSlider"
      id="slider"
    >
      <div class="container">
        <div
          class="close"
          @click="showSlider = !showSlider"
          @keypress="showSlider = !showSlider"
        >
          <svg
            data-v-35bcf9f0=""
            aria-hidden="true"
            focusable="false"
            data-prefix="fas"
            data-icon="xmark"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 384 512"
            class="svg-inline--fa fa-xmark fa-w-12"
          >
            <path
              data-v-35bcf9f0=""
              fill="currentColor"
              d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
              class=""
            />
          </svg>
        </div>

        <VSlickCarousel
          class="the-slider"
          :arrows="true"
          :dots="false"
          :center-mode="false"
          :adaptive-height="false"
          :variable-width="false"
          :groups-to-show="1"
          :groups-to-scroll="1"
          :initial-group-index="currentSlide"
          @after-change="(i) => currentSlide = i"
        >
          <template #prevArrow="arrowOption">
            <div class="custom-arrow v-slick-arrow prev" @click="arrowOption.onClick">
              {{ arrowOption.currentSlideGroupIndex }}/{{ arrowOption.slideGroupCount }}
            </div>
          </template>
          <div
            v-for="gallery in section.gallery"
            :key="gallery.ID"
          >
            <img
              :src="gallery.gallery_image.url"
              :alt="gallery.gallery_image.alt"
            >
          </div>
          <template #nextArrow="arrowOption">
            <div class="custom-arrow v-slick-arrow next" @click="arrowOption.onClick">
              {{ arrowOption.currentSlideGroupIndex }}/{{ arrowOption.slideGroupCount }}
            </div>
          </template>
        </VSlickCarousel>
      </div>
    </div>
  </div>
</template>

<script>
import RysEmbedModal from '@/components/modals/EmbedModal.vue';
import stripBlockTags from '@/utils/strip-block-tags';

export default {
  name: 'SplitImageSection',

  props: {
    section: { type: Object, required: true }
  },

  setup() {
    const modal = useModal();
    return { modal };
  },

  data() {
    return {
      showSlider: false,
      currentSlide: 0,
      screenWidth: 0
    };
  },

  computed: {
    btnClass() {
      if (this.section.button_text) {
        if (this.section.style === 'fancy') {
          return 'black';
        }

        if (this.section.bg_color === 'white') {
          return 'ghost';
        }
      }

      return '';
    }
  },

  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    this.onWindowResize();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },

  methods: {
    stripBlockTags,

    showEmbedModal(html) {
      this.modal(RysEmbedModal, this, { props: { embed: html } });
    },

    showSlide(i) {
      this.currentSlide = typeof i === 'number' ? i : parseInt(i);
      this.showSlider = true;
    },

    onLinkClick(e, section) {
      if (section.link_shows_video && section.video) {
        e.preventDefault();
        this.showEmbedModal(section.video);
      }
    },

    onWindowResize() {
      this.screenWidth = window.innerWidth;
    }
  }
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

div.split-image-section {
  position: relative;
  overflow-x: clip;

  .bg {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    .orange {
      position: relative;
      background-color: $thd-orange;
      height: 100%;
      width: 100%;
      transform: rotateZ(2deg) scale(110%);
      z-index: 2;

      @media screen and (min-width: $breakpoint-lg) {
        width: 50%;
        transform: rotateZ(2deg) scale(105%);
      }
    }

    .img {
      height: 100%;
      padding-top: 0;
      position: absolute;
      right: 0;
      width: 50%;
      z-index: 1;

      img {
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
    }
  }

  .container {
    position: relative;
    z-index: 2;
    display: grid;
    gap: 24px;
    grid-template-columns: minmax(auto, 100%);
    grid-template-rows: repeat(4, minmax(0, auto));

    @media screen and (min-width: $breakpoint-lg) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: repeat(2, auto) minmax(0, 1fr);
      grid-auto-flow: column dense;
      gap: 24px 60px;
    }

    .image-container,
    .video-container {
      @media screen and (min-width: $breakpoint-lg) {
        order: 9999;
        grid-row: span 3 / span 3;

        &.dir-left {
          order: -9999;
        }
      }

      .aspect {
        position: relative;
        width: 100%;
        padding-top: math.div(9, 16) * 100%;

        @media screen and (min-width: $breakpoint-lg) {
          padding-top: 100%;
        }

        > img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;

          &.clickable {
            cursor: pointer;
          }
        }
      }

      .embed::v-deep {
        width: 100%;
        height: 0;
        overflow: hidden;
        position: relative;
        padding-top: math.div(9, 16) * 100%;

        iframe {
          width: 100% !important;
          height: 100% !important;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
  }

  &.bg-white {
    background-color: $thd-white;

    h2 {
      color: $thd-black;
    }

    .content {
      color: $thd-dark-gray;
    }
  }

  &.bg-black {
    background-color: $thd-black;

    h2 {
      color: $thd-white;
    }

    .content {
      color: $thd-white;
    }
  }

  &.style-fancy {
    margin-top: 64px;

    @media screen and (min-width: $breakpoint-lg) {
      margin-top: 0;
    }

    .bg {
      display: flex;

      &.dir-left {
        flex-direction: row-reverse;

        .orange {
          transform: rotateZ(-2deg) scale(110%);

          @media screen and (min-width: $breakpoint-lg) {
            transform: rotateZ(-2deg) scale(105%);
          }
        }

        .img {
          left: 0;
          right: unset;
        }
      }
    }

    .container::v-deep {
      padding-top: 32px;

      @media screen and (min-width: $breakpoint-lg) {
        padding-top: 60px;
      }

      h2 {
        color: $thd-white;
      }

      .content {
        color: $thd-white;

        strong,
        b {
          color: $thd-black;
        }
      }

      .image-container {
        @media screen and (min-width: $breakpoint-lg) {
          visibility: hidden;
        }
      }
    }
  }
}

#gallery {
  background-color: #efefef;

  .container {
    padding-top: 60px;
    padding-bottom: 60px;
    margin-top: 0;

    @media screen and (min-width: $breakpoint-md) {
      padding-bottom: 50px;
      margin-top: 0;
    }
  }

  h2::v-deep {
    font-family: 'Yearbook Solid W01', sans-serif;
    font-size: 36px;
    line-height: 40px;
    color: $thd-black;
    margin: 0 0 40px;
    text-transform: uppercase;

    @media screen and (min-width: $breakpoint-md) {
      font-size: 60px;
      line-height: 70px;
    }

    strong {
      color: $thd-orange;
      font-weight: 700;
    }
  }

  .sub-text {
    margin-bottom: 40px;
    font-size: 20px;
  }

  .gallery-holder {
    display: grid;
    gap: 5px;
    grid-template-columns: repeat(5, 1fr);
    grid-auto-rows: 50px;

    @media screen and (min-width: $breakpoint-md) {
      gap: 10px;
      grid-auto-rows: 140px;
    }

    .grid-item:nth-child(1) {
      grid-column: span 2;
      grid-row: span 2;
    }

    .grid-item:nth-child(2) {
      grid-column: 3;
      grid-row: 1;
    }

    .grid-item:nth-child(3) {
      grid-column: 3;
      grid-row: 2;
    }

    .grid-item:nth-child(4) {
      grid-column: span 2;
      grid-row: span 2;
    }

    .grid-item:nth-child(5) {
      grid-column: span 3;
      grid-row: span 2;
    }

    .grid-item:nth-child(6) {
      grid-column: 4;
      grid-row: 3;

      img {
        object-position: 50% 22%;
      }
    }

    .grid-item:nth-child(7) {
      grid-column: 4;
      grid-row: 4;
    }

    .grid-item:nth-child(8) {
      grid-column: 5;
      grid-row: span 2;
    }

    .grid-item:nth-child(9) {
      grid-column: span 2;
      grid-row: span 2;
    }

    .grid-item:nth-child(10) {
      grid-column: 3;
      grid-row: 5;
    }

    .grid-item:nth-child(11) {
      grid-column: 3;
      grid-row: 6;
    }

    .grid-item:nth-child(12) {
      grid-column: span 2;
      grid-row: span 2;
    }

    .grid-item:nth-child(13) {
      grid-column: span 3;
      grid-row: span 2;

      img {
        object-position: 50% 30%;
      }
    }

    .grid-item:nth-child(14) {
      grid-column: 4;
      grid-row: 7;

      img {
        object-position: 50% 30%;
      }
    }

    .grid-item:nth-child(15) {
      grid-column: 4;
      grid-row: 8;

      img {
        object-position: 50% 30%;
      }
    }

    .grid-item:nth-child(16) {
      grid-column: 5;
      grid-row: span 2;

      img {
        object-position: 50% 30%;
      }
    }

    .grid-item:nth-child(17) {
      grid-column: span 2;
      grid-row: span 2;

      img {
        object-position: 50% 21%;
      }
    }

    .grid-item:nth-child(18) {
      grid-column: 3;
      grid-row: 9;

      img {
        object-position: 50% 30%;
      }
    }

    .grid-item:nth-child(19) {
      grid-column: 3;
      grid-row: 10;

      img {
        object-position: 50% 30%;
      }
    }

    .grid-item:nth-child(20) {
      grid-column: span 2;
      grid-row: span 2;

      img {
        object-position: 50% 30%;
      }
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
    }
  }
}

#slider {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background-color: rgb(0 0 0 / 75%);

  .container {
    height: calc(100% - 200px);
    padding-top: 100px;
    padding-bottom: 100px;

    .close {
      position: absolute;
      top: 25px;
      right: 25px;
      cursor: pointer;
      font-size: xx-large;
      z-index: 10;
      color: white;

      @media screen and (min-width: $breakpoint-md) {
        top: 104px;
      }
    }

    .the-slider::v-deep {
      height: 100%;

      .v-slick-arrow.next,
      .v-slick-arrow.prev {
        height: 50px !important;
        width: 50px !important;
        display: none;

        @media screen and (min-width: $breakpoint-md) {
          display: block;
        }
      }

      .v-slick-arrow.prev {
        left: -50px !important;
      }

      .v-slick-arrow.next {
        right: -50px !important;
      }

      .v-slick-arrow.next::before {
        font-size: 50px !important;
      }

      .v-slick-arrow.prev::before {
        font-size: 50px !important;
      }

      .v-slick-slider {
        height: 100%;
      }

      .v-slick-list {
        height: 100% !important;
      }

      .v-slick-track {
        height: 100%;
      }

      .v-slick-slide-group {
        display: flex;
        justify-content: center;
        align-items: center;

        > div {
          width: 100%;
          height: 100%;
        }
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
      }

      .v-slick-dots {
        display: none !important;
      }

      .v-slick-dots li .custom-dot {
        color: #ffffff;
        opacity: 0.75;
        font-size: 50px;
      }

      .v-slick-dots li.active .custom-dot {
        color: #ffffff;
        opacity: 1;
      }
    }
  }
}
</style>
